import {useMemo, useContext} from 'react'
import {learningCenterPageTypes, addStaticCrumbs} from './utils'
import {generateBreadcrumbLinkContent} from '../utils'
import {BuilderContext} from '@builder.io/sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useFetchParentLevel from 'hec/hooks/learning-center/useFetchParentLevel'
import useFetchParentGoal from 'hec/hooks/learning-center/useFetchParentGoal'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import modelMap from 'hec/builder/model-map'

export const useLearningCenterPage = () => {
    const config = getConfig()
    const builderContext = useContext(BuilderContext)

    const pageData = builderContext?.content?.data
    const {pageType} = pageData

    const shouldQueryGoalData = pageType === learningCenterPageTypes.GOAL && !pageData?.goal?.value
    const shouldQueryModuleData =
        pageType === learningCenterPageTypes.MODULE && !pageData?.module?.value

    const {
        data: goalData,
        isLoading: isGoalDataLoading,
        error: goalDataError
    } = useFetchBuilderReference({
        model: modelMap.learningCenterGoal,
        shouldQuery: shouldQueryGoalData,
        referenceId: pageData?.goal?.id,
        fields: 'data.title,data.dashboard'
    })

    const {
        data: moduleData,
        isLoading: isModuleDataLoading,
        error: moduleDataError
    } = useFetchBuilderReference({
        model: modelMap.learningCenterModule,
        shouldQuery: shouldQueryModuleData,
        referenceId: pageData?.module?.id,
        fields: 'data.id,data.page'
    })

    const {
        data: moduleLevel,
        isLoading: isModuleLevelLoading,
        error: moduleLevelError
    } = useFetchParentLevel({
        moduleContentId: pageData?.module?.id,
        apiKey: config.app.builder.api,
        shouldQuery: shouldQueryModuleData,
        fields: 'id'
    })

    const {
        data: moduleGoal,
        isLoading: isModuleGoalLoading,
        error: moduleGoalError
    } = useFetchParentGoal({
        levelContentId: moduleLevel?.id,
        apiKey: config.app.builder.api,
        shouldQuery: shouldQueryModuleData,
        fields: 'data.title,data.dashboard'
    })

    const isLoading =
        (isModuleDataLoading && shouldQueryModuleData) ||
        (isModuleGoalLoading && shouldQueryModuleData) ||
        (isModuleLevelLoading && shouldQueryModuleData) ||
        (isGoalDataLoading && shouldQueryGoalData)

    const error = moduleDataError || moduleGoalError || moduleLevelError || goalDataError

    if (error) {
        throw new Error(
            'An error has occurred while fetching data for the Learning Center Breadcrumb component:',
            error
        )
    }

    const goal = shouldQueryGoalData ? goalData?.data : pageData?.goal?.value?.data
    const module = shouldQueryModuleData ? moduleData?.data : pageData?.module?.value?.data

    const learningCenterCrumbs = useMemo(() => {
        const crumbs = []
        if (isLoading || !pageData) return crumbs
        switch (pageType) {
            case learningCenterPageTypes.GOAL: {
                if (goal) {
                    const {title, dashboard} = goal
                    const crumbContent = generateBreadcrumbLinkContent({
                        title,
                        pageData: dashboard,
                        isClickable: false
                    })
                    crumbs.push(crumbContent)
                    return addStaticCrumbs(crumbs)
                }
                break
            }
            case learningCenterPageTypes.MODULE:
                {
                    if (module && moduleGoal?.data) {
                        const {data: moduleGoalData} = moduleGoal || {}

                        const {dashboard, title: goalTitle} = moduleGoalData
                        const goalCrumbData = generateBreadcrumbLinkContent({
                            pageData: dashboard,
                            title: `Goal: ${goalTitle}`,
                            isClickable: true
                        })

                        const match = module.id.match(/lvl-(\d+)/)
                        const levelNumber = match ? match[1] : null
                        const moduleNumber = module.id[module.id.length - 1]
                        const modulePageData = module.page?.value?.data
                        const moduleCrumbData = generateBreadcrumbLinkContent({
                            pageData: modulePageData,
                            title: `Level ${levelNumber}: Module ${moduleNumber}`,
                            isClickable: false
                        })
                        crumbs.push(goalCrumbData, moduleCrumbData)
                        return addStaticCrumbs(crumbs)
                    }
                }
                break

            case learningCenterPageTypes.DEFAULT:
                {
                    const {title} = pageData
                    if (title) {
                        const crumbContent = generateBreadcrumbLinkContent({
                            title,
                            isClickable: false
                        })
                        crumbs.push(crumbContent)
                        return addStaticCrumbs(crumbs)
                    }
                }
                break
        }

        return crumbs
    }, [pageType, pageData, goal, module, moduleGoal, isLoading])

    return {crumbs: learningCenterCrumbs}
}
